import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  useGetCompanyPurchaseDetailsMutation,
  useGetCompanyWorldboxstatusMutation,
} from "../../../../store/Company/companyApiSlice";
import { statusLadder, profileStatus, WARNING, LISTING } from "./profileStatus";

/* The purchase and WorldBox status behind the masthead's buttons. */

/* Transcribed verbatim, branch order included. An enterprise purchase is
   flattened; otherwise a personal purchase row is used as it stands; otherwise
   whatever the enterprise key holds, even when that is nothing. */
export const flattenPurchase = (res) => {
  const enterprise = res?.data?.enterprise;
  const purchase = res?.data?.purchase;

  if (enterprise?.purchaseID) {
    return {
      purchaseID: enterprise.purchaseID,
      companyID: enterprise.companyID,
      planID: enterprise.planID,
      purchaseDate: enterprise.purchaseDate,
      expireDate: enterprise.expireDate,
      purchaseType: enterprise.purchaseType,
      wb_id: enterprise.wb_id,
      userID: enterprise.userID,
      status: enterprise.status,
    };
  }
  if (purchase?.purchaseID) return purchase;
  return enterprise;
};

/* Transcribed: a personal purchase is a Subscription row with an id. */
export const hasPersonalPurchase = (res) =>
  res?.data?.purchase?.purchaseType === "Subscription" &&
  Boolean(res?.data?.purchase?.purchaseID);

/* `statusData` in the live banner. Now `statusLadder` in profileStatus.js,
   alongside the chain it falls back to; kept here under its original name
   because that is what this file's callers import. */
export const enterpriseStatusData = statusLadder;

export const usePurchaseStatus = (company) => {
  const { id } = useParams();
  const { token, userInfo } = useSelector((state) => state.auth);

  const [getPurchaseDetailsStatus] = useGetCompanyPurchaseDetailsMutation();
  const [checkWorldboxStatus] = useGetCompanyWorldboxstatusMutation();

  const [purchaseDetails, setPurchaseDetails] = useState(undefined);
  const [personalPurchase, setPersonalPurchase] = useState(false);
  const [worldboxStatus, setWorldboxStatus] = useState("");
  /* Transcribed: the effect re-runs when the enterprise flow flips this, which
     is how the button relabels itself after a company is added. */
  const [trigger, setTrigger] = useState(false);

  const refresh = useCallback(() => setTrigger((t) => !t), []);

  useEffect(() => {
    if (!company) return undefined;
    let cancelled = false;
    const userID = userInfo?.individual?.[0]?.userID;

    (async () => {
      const status = profileStatus(company);

      /* Transcribed: only a listing or a draft has an order to wait on;
         anything else is available outright. */
      if (status === WARNING || status === LISTING) {
        try {
          const res = await checkWorldboxStatus({ companyID: id, userID });
          if (!cancelled && res?.data?.status === "COMPLETED") {
            setWorldboxStatus("available");
          }
        } catch {
          /* The live effect swallows this. Kept: the flag simply stays unset,
             which is the same as "not available yet". */
        }
      } else if (!cancelled) {
        setWorldboxStatus("available");
      }

      try {
        const res = await getPurchaseDetailsStatus({ companyID: id, userID, token });
        if (cancelled) return;
        if (hasPersonalPurchase(res)) setPersonalPurchase(true);
        setPurchaseDetails(flattenPurchase(res));
      } catch {
        /* As the live effect does. The buttons fall back to their unbought
           state, which is the safe reading. */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [company, userInfo, id, token, trigger, getPurchaseDetailsStatus, checkWorldboxStatus]);

  return {
    /* The FLATTENED shape the enterprise button and the subscription pill
       read. Not the raw query — see the note above. */
    purchaseDetails,
    personalPurchase,
    worldboxStatus,
    worldboxAvailable: worldboxStatus === "available",
    statusData: enterpriseStatusData(company),
    trigger,
    refresh,
    setTrigger,
  };
};

export default usePurchaseStatus;
